import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Search, FileText, Loader2, MoreVertical, ArrowRight, Trash2, Play, CheckSquare, Square } from 'lucide-react'
import { FileUploadZone, UploadProgressList } from './FileUploadZone'
import { BatchRecoveryBanner } from './BatchRecoveryBanner'
import type { UploadedFileEntry, FileStatus } from './types'
import { getStatusConfig } from './utils'
import type { PersistedBatchState } from '@/hooks/usePersistedBatch'

interface FilesTabProps {
  files: UploadedFileEntry[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  search: string
  statusFilter: FileStatus | 'all'
  onUpload: (files: FileList) => Promise<void>
  onExtract: (fileId: string) => Promise<void>
  onBulkExtract: (fileIds: string[]) => Promise<void>
  onDelete: (fileId: string) => Promise<void>
  onBulkDelete: (fileIds: string[]) => Promise<void>
  onSelectForImport: (fileId: string) => void
  onSearchChange: (search: string) => void
  onPageChange: (page: number) => void
  onStatusFilterChange: (filter: FileStatus | 'all') => void
  onGoToEntries?: () => void
  onClearSelection?: () => void
  onContinueBatch?: (batch: PersistedBatchState) => void | Promise<void>
}

export function FilesTab({
  files,
  loading,
  total,
  page,
  pageSize,
  search,
  statusFilter,
  onUpload,
  onExtract,
  onBulkExtract,
  onDelete,
  onBulkDelete,
  onSelectForImport,
  onSearchChange,
  onPageChange,
  onStatusFilterChange,
  onContinueBatch,
}: FilesTabProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null)
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const [uploadingFiles, setUploadingFiles] = useState<Array<{ name: string; progress?: number; error?: string }>>([])
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    return () => setUploadingFiles([])
  }, [])

  const toggleSelect = useCallback((fileId: string) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev)
      if (next.has(fileId)) {
        next.delete(fileId)
      } else {
        next.add(fileId)
      }
      return next
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedFileIds(new Set())
  }, [])

  const handleBulkExtract = useCallback(async () => {
    if (selectedFileIds.size === 0) return
    await onBulkExtract(Array.from(selectedFileIds))
    clearSelection()
  }, [selectedFileIds, onBulkExtract, clearSelection])

  const handleBulkDelete = useCallback(async () => {
    if (selectedFileIds.size === 0) return
    const count = selectedFileIds.size
    if (confirm(`${count} Dateien wirklich löschen?`)) {
      await onBulkDelete(Array.from(selectedFileIds))
      clearSelection()
    }
  }, [selectedFileIds, onBulkDelete, clearSelection])

  const filteredFiles = useMemo(() => {
    let result = files
    if (statusFilter !== 'all') {
      result = result.filter(f => f.status === statusFilter)
    }
    if (search) {
      result = result.filter(f =>
        f.original_filename.toLowerCase().includes(search.toLowerCase()) ||
        (f.error || '').toLowerCase().includes(search.toLowerCase())
      )
    }
    return result
  }, [files, statusFilter, search])

  const toggleSelectAll = useCallback(() => {
    if (selectedFileIds.size === filteredFiles.length && filteredFiles.length > 0) {
      setSelectedFileIds(new Set())
    } else {
      setSelectedFileIds(new Set(filteredFiles.map(f => f.id)))
    }
  }, [filteredFiles, selectedFileIds.size])

  const handleAction = useCallback(async (fileId: string, action: 'extract' | 'delete' | 'import') => {
    setActionLoading(fileId)
    setOpenMenu(null)
    setMenuPosition(null)
    try {
      if (action === 'extract') await onExtract(fileId)
      else if (action === 'delete') await onDelete(fileId)
      else if (action === 'import') onSelectForImport(fileId)
    } finally {
      setActionLoading(null)
    }
  }, [onExtract, onDelete, onSelectForImport])

  const toggleMenu = useCallback((fileId: string, button: HTMLButtonElement | null) => {
    if (openMenu === fileId) {
      setOpenMenu(null)
      setMenuPosition(null)
    } else {
      setOpenMenu(fileId)
      if (button) {
        const rect = button.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        })
      }
    }
  }, [openMenu])

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenu(null)
      setMenuPosition(null)
    }
    if (openMenu) {
      window.addEventListener('scroll', handleClickOutside, true)
      window.addEventListener('resize', handleClickOutside)
      return () => {
        window.removeEventListener('scroll', handleClickOutside, true)
        window.removeEventListener('resize', handleClickOutside)
      }
    }
    return undefined
  }, [openMenu])

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="space-y-4">
      {/* Batch Recovery Banner - shows pending batches */}
      {onContinueBatch && (
        <BatchRecoveryBanner onContinue={onContinueBatch} />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Dateien laden...</span>
        </div>
      )}

      {!loading && (
      <>
      {/* Upload Bereich */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Dateien hochladen</h2>
        <FileUploadZone onUpload={async (fileList) => {
          setUploadingFiles(Array.from(fileList).map(f => ({ name: f.name, progress: 0 })))
          try {
            await onUpload(fileList)
            setUploadingFiles(prev => prev.map(f => ({ ...f, progress: 100 })))
            setTimeout(() => setUploadingFiles([]), 2000)
          } catch (err: unknown) {
            const e = err as { response?: { data?: { detail?: { message?: string } | string } }; message?: string }
            const detail = e.response?.data?.detail
            const msg = typeof detail === 'object' ? detail?.message : detail || e.message || 'Upload fehlgeschlagen'
            setUploadingFiles(prev => prev.map(f => ({ ...f, error: typeof msg === 'object' ? JSON.stringify(msg) : msg })))
          }
        }} />
        <UploadProgressList files={uploadingFiles} />
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Dateien suchen..."
            className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => onStatusFilterChange(statusFilter !== 'all' ? 'all' : 'idle')}
          className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
            statusFilter !== 'all' ? 'border-blue-500 bg-blue-50 text-blue-600' : 'hover:bg-gray-50'
          }`}
        >
          {statusFilter === 'all' ? 'Alle' : statusFilter}
        </button>
      </div>

      {/* Bulk Selection Bar */}
      {selectedFileIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-700">
            {selectedFileIds.size} {selectedFileIds.size === 1 ? 'Datei' : 'Dateien'} ausgewählt
          </span>
          <button
            onClick={handleBulkExtract}
            disabled={actionLoading !== null}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            Extrahieren
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={actionLoading !== null}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Löschen
          </button>
          <button
            onClick={clearSelection}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 ml-auto"
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* Tabelle */}
      {files.length === 0 ? (
        <div className="py-12 text-center">
          <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Dateien vorhanden</h3>
          <p className="text-sm text-gray-500">
            Lade dein erstes Stundenplan-PDF hoch um zu beginnen.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
               <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                      disabled={filteredFiles.length === 0}
                    >
                      {selectedFileIds.size === filteredFiles.length && filteredFiles.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Datum</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 text-xs uppercase tracking-wider hidden lg:table-cell">Grösse</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500 text-xs uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredFiles.map(file => {
                  const statusConfig = getStatusConfig(file.status)
                  if (!statusConfig) return null
                  const StatusIcon = statusConfig.icon
                  const isSelected = selectedFileIds.has(file.id)
                  const isLoading = actionLoading === file.id
                  // TODO: Delete after V2 migration
                  // const canExtract = file.status === 'idle' || file.status === 'error' || file.status === 'structure_ok'
                  const canImport = file.status === 'structure_ok' || file.status === 'entry_ok' || file.status === 'imported'

                  return (
                    <tr
                      key={file.id}
                      className={`transition-colors ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                      } ${
                        file.status === 'error' ? 'bg-red-50/50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelect(file.id)}
                          className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate max-w-[200px] sm:max-w-xs" title={file.original_filename}>
                              {file.original_filename}
                            </p>
                            {file.error && (
                              <p className="text-xs text-red-500 truncate max-w-[200px] sm:max-w-xs" title={file.error}>
                                {file.error}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell whitespace-nowrap">
                        {formatDate(file.uploaded_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell whitespace-nowrap">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          {file.status === 'extracting' || file.status === 'uploading' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <StatusIcon className="w-3 h-3" />
                          )}
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canImport && (
                            <button
                              onClick={() => handleAction(file.id, 'import')}
                              disabled={isLoading}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Für Import verwenden"
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                            </button>
                          )}
                           {/* TODO: Delete after V2 migration - replaced by PostUploadDialog flow */}
                           {/* canExtract && (
                             <button
                               onClick={() => handleAction(file.id, 'extract')}
                               disabled={isLoading}
                               className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                               title="Extrahieren"
                             >
                               {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                 file.status === 'structure_ok' ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" />
                               )}
                             </button>
                           ) */}
                          <div className="relative">
                            <button
                              ref={(el) => {
                                if (el) buttonRefs.current.set(file.id, el)
                                else buttonRefs.current.delete(file.id)
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleMenu(file.id, e.currentTarget)
                              }}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Mehr Optionen"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {openMenu && menuPosition && (
            <div
              className="fixed w-48 bg-white border rounded-lg shadow-lg z-50 py-1"
              style={{ top: menuPosition.top, right: menuPosition.right }}
            >
              <button
                onClick={() => handleAction(openMenu, 'import')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                Für Import verwenden
              </button>
              {/* TODO: Delete after V2 migration - replaced by PostUploadDialog flow */}
              {/* files.find(f => f.id === openMenu) && (
                files.find(f => f.id === openMenu)!.status === 'idle' ||
                files.find(f => f.id === openMenu)!.status === 'error' ||
                files.find(f => f.id === openMenu)!.status === 'structure_ok'
              ) && (
                <button
                  onClick={() => handleAction(openMenu, 'extract')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Play className="w-3.5 h-3.5" />
                  Extrahieren
                </button>
              ) */}
              <button
                onClick={() => {
                  const file = files.find(f => f.id === openMenu)
                  if (file && confirm(`"${file.original_filename}" wirklich löschen?`)) {
                    handleAction(openMenu, 'delete')
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Löschen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {total} Dateien gesamt
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Zurück
            </button>
            <span className="text-sm text-gray-500">
              Seite {page} von {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Weiter
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE')
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
